import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  api, ApiFailure, ageLabel, genderShort, downloadFile,
  VISIT_TYPE_LABEL, VISIT_STATUS_LABEL, PATIENT_CATEGORIES, PATIENT_CATEGORY_LABEL,
  type Doctor, type RecordRow,
} from '../api'
import { CategoryPill } from '../components/PatientCategory'

const PAGE = 50

function today(): string {
  return new Date().toLocaleDateString('en-CA') // YYYY-MM-DD in local time
}

function money(v: string | null): string {
  if (v == null) return '—'
  const n = Number(v)
  return Number.isFinite(n) ? `₹${n.toFixed(0)}` : '—'
}

const BLANK = {
  from: '', to: '', doctorId: '', visitType: '', status: '',
  paymentStatus: '', category: '', q: '', diagnosis: '',
}

type Filters = typeof BLANK

export default function Records() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const patientId = params.get('patient')

  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [filters, setFilters] = useState<Filters>({ ...BLANK, from: today(), to: today() })
  const [rows, setRows] = useState<RecordRow[]>([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [patientName, setPatientName] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    void api.get<{ doctors: Doctor[] }>('/api/doctors').then((r) => setDoctors(r.doctors))
  }, [])

  const queryString = useCallback((extra?: Record<string, string>) => {
    const p = new URLSearchParams()
    if (patientId) {
      p.set('patientId', patientId)
    } else {
      for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v)
    }
    for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v)
    return p.toString()
  }, [filters, patientId])

  const load = useCallback(async (nextOffset: number) => {
    setBusy(true)
    setMsg(null)
    try {
      const qs = queryString({ limit: String(PAGE), offset: String(nextOffset) })
      const res = await api.get<{ visits: RecordRow[]; total: number }>(`/api/records/visits?${qs}`)
      setRows(res.visits)
      setTotal(res.total)
      setOffset(nextOffset)
      if (patientId) setPatientName(res.visits[0]?.patientName ?? 'this patient')
    } catch (err) {
      setMsg(err instanceof ApiFailure ? err.message : 'Could not load records.')
    } finally {
      setBusy(false)
    }
  }, [queryString, patientId])

  // Reload whenever the filters or the timeline target change.
  useEffect(() => { void load(0) }, [load])

  function setView(view: 'today' | 'all') {
    if (patientId) { params.delete('patient'); setParams(params, { replace: true }) }
    setFilters(view === 'today'
      ? { ...BLANK, from: today(), to: today() }
      : { ...BLANK })
  }

  function openTimeline(row: RecordRow) {
    setParams({ patient: row.patientId }, { replace: true })
  }

  function exitTimeline() {
    params.delete('patient')
    setParams(params, { replace: true })
    setPatientName(null)
  }

  async function exportCsv() {
    try {
      await downloadFile(`/api/records/visits.csv?${queryString()}`, `visits-${today()}.csv`)
    } catch (err) {
      setMsg(err instanceof ApiFailure ? err.message : 'Export failed.')
    }
  }

  const pages = Math.max(1, Math.ceil(total / PAGE))
  const page = Math.floor(offset / PAGE) + 1

  return (
    <>
      <div className="page-head">
        <h2>Records</h2>
        <p>Every visit, searchable. The timeline view is the patient&rsquo;s file.</p>
      </div>

      {msg && <div className="msg error">{msg}</div>}

      <div className="panel">
        {patientId ? (
          <div className="records-views">
            <span className="pill on">Timeline — {patientName ?? '…'}</span>
            <button className="link" onClick={exitTimeline}>← Back to all visits</button>
          </div>
        ) : (
          <>
            <div className="records-views">
              <button
                className={filters.from === today() && filters.to === today() ? 'chip is-on' : 'chip'}
                onClick={() => setView('today')}
              >
                Today
              </button>
              <button
                className={!filters.from && !filters.to ? 'chip is-on' : 'chip'}
                onClick={() => setView('all')}
              >
                All visits
              </button>
              <span className="spacer" />
              <button className="secondary" disabled={busy || rows.length === 0} onClick={() => void exportCsv()}>
                Export CSV
              </button>
            </div>

            <div className="records-filters">
              {/* max/min tie the two boxes together, so a backwards range
                  cannot be entered in the first place. A range that reads
                  right but returns nothing looks like missing data. */}
              <input type="date" aria-label="From" value={filters.from} max={filters.to || undefined}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              <input type="date" aria-label="To" value={filters.to} min={filters.from || undefined}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              <select aria-label="Doctor" value={filters.doctorId}
                onChange={(e) => setFilters((f) => ({ ...f, doctorId: e.target.value }))}>
                <option value="">All doctors</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
              </select>
              <select aria-label="Visit type" value={filters.visitType}
                onChange={(e) => setFilters((f) => ({ ...f, visitType: e.target.value }))}>
                <option value="">Any type</option>
                {Object.entries(VISIT_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select aria-label="Payment" value={filters.paymentStatus}
                onChange={(e) => setFilters((f) => ({ ...f, paymentStatus: e.target.value }))}>
                <option value="">Any payment</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="unpaid">Unpaid</option>
                <option value="refunded">Refunded</option>
              </select>
              <select aria-label="Patient category" value={filters.category}
                onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}>
                <option value="">All patients</option>
                {PATIENT_CATEGORIES.map((c) => <option key={c} value={c}>{PATIENT_CATEGORY_LABEL[c]}</option>)}
              </select>
              <input placeholder="Name, mobile or code" value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} />
              <input placeholder="Diagnosis" value={filters.diagnosis}
                onChange={(e) => setFilters((f) => ({ ...f, diagnosis: e.target.value }))} />
            </div>
          </>
        )}

        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="records-table">
            <thead>
              <tr>
                <th>Date</th><th>Tok</th><th>Patient</th><th>Age/Sex</th><th>Category</th><th>Doctor</th>
                <th>Visit</th><th>Diagnosis</th><th className="num">Fee</th><th className="num">Paid</th>
                <th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="nowrap">{r.visitDate.slice(5)}</td>
                  <td className="num">#{r.tokenNo}</td>
                  <td>
                    <div className="rec-name">{r.patientName}</div>
                    <div className="rec-code">{r.patientCode}</div>
                  </td>
                  <td className="nowrap">{ageLabel(r)}/{genderShort(r.gender)}</td>
                  <td className="nowrap"><CategoryPill category={r.category} /></td>
                  <td className="nowrap">{r.doctorName.replace(/^Dr\.?\s*/, 'Dr ')}</td>
                  <td className="nowrap">#{r.visitNo} {VISIT_TYPE_LABEL[r.visitType]}</td>
                  <td>{r.diagnosis ?? '—'}</td>
                  <td className="num">{money(r.fee)}</td>
                  <td className="num">{money(r.paid)}</td>
                  <td><span className={`pill ${r.status === 'completed' ? 'on' : 'off'}`}>{VISIT_STATUS_LABEL[r.status]}</span></td>
                  <td className="nowrap">
                    <button className="link" onClick={() => navigate(`/visits/${r.id}`)}>Open</button>
                    {!patientId && <button className="link" onClick={() => openTimeline(r)}>Timeline</button>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && !busy && (
                <tr>
                  <td colSpan={11}>
                    <div className="empty">
                      {filters.from && filters.to && filters.from > filters.to
                        ? `No visits match — the range runs backwards, from ${filters.from} to ${filters.to}.`
                        : 'No visits match.'}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="records-foot">
          <span className="rec-count">{total} visit{total === 1 ? '' : 's'}</span>
          {pages > 1 && (
            <span className="pager">
              <button className="secondary" disabled={offset === 0 || busy} onClick={() => void load(offset - PAGE)}>Prev</button>
              <span>Page {page} of {pages}</span>
              <button className="secondary" disabled={page >= pages || busy} onClick={() => void load(offset + PAGE)}>Next</button>
            </span>
          )}
        </div>
      </div>
    </>
  )
}
